import React, { useEffect, useState, useRef } from 'react';
import {
    AdminBlockConfig,
    LayoutStyle,
    ContentSourceType,
    BLOCK_DEFINITIONS,
    BlockDefinition,
} from '../../types/homepage';
import {
    useAdminBlocks,
    useCreateBlock,
    useUpdateBlock,
    useDeleteBlock,
    useToggleBlock,
    useReorderBlocks,
} from './useHomepageMock';
import BlockPickerModal from './BlockPickerModal';
import { configFromBlock, SharedConfigPanel, defaultConfig, BlockConfig } from './blockConfig.shared';
import { getBlockRenderer, configToRow } from './registry';
import { successMsg } from '../utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
function buildSettingsFromConfig(type: string, config: Record<string, unknown>): Record<string, unknown> {
    let result: Record<string, unknown> = {};

    switch (type) {
        case 'welcome-banner':
            result = {
                backgroundImage: config.backgroundImage,
                overlayOpacity: config.overlayOpacity,
                eyebrow: config.eyebrow,
                ctaLabel: config.ctaLabel,
                ctaUrl: config.ctaUrl,
                ctaSecondaryLabel: config.ctaSecondaryLabel,
                ctaSecondaryUrl: config.ctaSecondaryUrl,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
                titleColor: config.titleColor,
                subtitleColor: config.subtitleColor,
                ctaBgColor: config.ctaBgColor,
                ctaTextColor: config.ctaTextColor,
                ctaSecondaryBgColor: config.ctaSecondaryBgColor,
                ctaSecondaryTextColor: config.ctaSecondaryTextColor,
            };
            break;
        case 'search-filter':
            result = {
                searchPlaceholder: config.searchPlaceholder,
                dropdowns: config.dropdowns,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
            break;
        case 'highlighted-categories':
            result = {
                tileColor: config.tileColor,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
            break;
        case 'header':
            result = {
                logoText: config.logoText,
                logoImage: config.logoImage,
                logoUrl: config.logoUrl,
                navLinks: config.navLinks,
                topBarLinks: config.topBarLinks,
                showSearchInHeader: config.showSearchInHeader,
                searchPlaceholder: config.searchPlaceholder,
                topBarBgColor: config.topBarBgColor,
                topBarTextColor: config.topBarTextColor,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
                ctaLabel: config.ctaLabel,
                ctaUrl: config.ctaUrl,
                ctaBgColor: config.ctaBgColor,
                ctaTextColor: config.ctaTextColor,
                ctaSecondaryLabel: config.ctaSecondaryLabel,
                ctaSecondaryUrl: config.ctaSecondaryUrl,
                ctaSecondaryBgColor: config.ctaSecondaryBgColor,
                ctaSecondaryTextColor: config.ctaSecondaryTextColor,
                showAccountMenu: config.showAccountMenu,
                accountAvatarType: config.accountAvatarType,
                accountAvatarImage: config.accountAvatarImage,
                accountAvatarBgColor: config.accountAvatarBgColor,
                accountAvatarTextColor: config.accountAvatarTextColor,
                accountName: config.accountName,
                accountEmail: config.accountEmail,
                accountMenuItems: config.accountMenuItems,
                showLogoutInMenu: config.showLogoutInMenu,
                logoutLabel: config.logoutLabel,
                accountMenuBgColor: config.accountMenuBgColor,
                accountMenuTextColor: config.accountMenuTextColor,
            };
            break;
        case 'footer':
            result = {
                logoText: config.logoText,
                logoImage: config.logoImage,
                logoUrl: config.logoUrl,
                copyrightText: config.copyrightText,
                footerColumns: config.footerColumns,
                socialLinks: config.socialLinks,
                socialTitle: config.socialTitle,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
                linkColor: config.linkColor,
                socialIconColor: config.socialIconColor,
            };
            break;
        case 'medium-carousel':
        case 'horizontal-scroll':
        case 'vertical-list':
        case 'thumbnail-grid':
            result = {
                showDuration: config.showDuration,
                showViewCount: config.showViewCount,
                showChannelName: config.showChannelName,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
            break;
        case 'text-over-video':
            result = {
                showDescription: config.showDescription,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
            break;
        case 'hero-carousel':
            result = {
                autoPlay: config.autoPlay,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
            break;
        case 'custom-promo':
            result = {
                htmlContent: config.htmlContent,
                backgroundColor: config.backgroundColor,
            };
            break;
        default:
            result = {
                ctaButtonLabel: config.ctaButtonLabel,
                ctaButtonUrl: config.ctaButtonUrl,
                backgroundColor: config.backgroundColor,
                textColor: config.textColor,
            };
    }

    result.fullWidth = config.fullWidth;

    return Object.fromEntries(
        Object.entries(result).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
}

const PANEL_WIDTH = 420;
const CONTAINED_MAX_WIDTH = 1200;

interface DragState {
    draggedId: string | null;
    overId: string | null;
}


// MAIN PAGE BUILDER
export default function PageBuilderAdmin() {
    const { data: blocks = [], isLoading, error } = useAdminBlocks();
    const createBlock = useCreateBlock();
    const updateBlock = useUpdateBlock();
    const reorderBlocks = useReorderBlocks();

    const [drag, setDrag] = useState<DragState>({ draggedId: null, overId: null });
    const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
    const [editConfig, setEditConfig] = useState<BlockConfig | null>(null);
    const [showBlockPicker, setShowBlockPicker] = useState(false);
    const [localBlocks, setLocalBlocks] = useState<AdminBlockConfig[] | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    const displayBlocks = localBlocks ?? [...blocks].sort((a, b) => a.sortOrder - b.sortOrder);
    const editingBlock = displayBlocks.find((b) => b.id === editingBlockId) || null;
    const editingDef = editingBlock ? BLOCK_DEFINITIONS.find((d) => d.type === editingBlock.type) : null;

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(null), 2200);
    };

    useEffect(() => {
        if (toast) {
            successMsg(toast)
        }
    }, [toast])
    //Editor panel

    const openEditor = (block: AdminBlockConfig) => {
        setEditingBlockId(block.id);
        setEditConfig(configFromBlock(block));
    };

    const closeEditor = () => {
        setEditingBlockId(null);
        setEditConfig(null);
    };

    const handleSaveEdit = () => {
        if (!editingBlock || !editConfig) return;
        updateBlock.mutate(
            {
                id: editingBlock.id,
                payload: {
                    title: editConfig.title || undefined,
                    subtitle: editConfig.subtitle || undefined,
                    layoutStyle: editConfig.layoutStyle,
                    contentSource: {
                        type: editConfig.contentSourceType as ContentSourceType,
                        limit: editConfig.contentLimit,
                    },
                    content: editConfig.content,
                    settings: buildSettingsFromConfig(editingBlock.type, editConfig as any),
                },
            },
            { onSuccess: () => showToast('Changes saved') }
        );
    };

    // Drag handlers 
    const handleDragStart = (e: React.DragEvent, id: string) => {
        e.dataTransfer.effectAllowed = 'move';
        setDrag((d) => ({ ...d, draggedId: id }));
    };

    const handleDragOver = (e: React.DragEvent, id: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (drag.overId !== id) setDrag((d) => ({ ...d, overId: id }));
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        const { draggedId } = drag;
        if (!draggedId || draggedId === targetId) {
            setDrag({ draggedId: null, overId: null });
            return;
        }

        const reordered = [...displayBlocks];
        const fromIdx = reordered.findIndex((b) => b.id === draggedId);
        const toIdx = reordered.findIndex((b) => b.id === targetId);
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);

        const withOrder = reordered.map((b, i) => ({ ...b, sortOrder: i + 1 }));
        setLocalBlocks(withOrder);
        setDrag({ draggedId: null, overId: null });

        reorderBlocks.mutate(
            { blocks: withOrder.map((b) => ({ id: b.id, sortOrder: b.sortOrder })) },
            {
                onSuccess: () => {
                    setLocalBlocks(null);
                    showToast('Order saved')
                },
                onError: () => setLocalBlocks(null),
            }
        );
    };

    const handleDragEnd = () => setDrag({ draggedId: null, overId: null });

    // Add block
    const handleAddBlock = (def: BlockDefinition) => {
        const layoutStyle = (def.supportedLayouts[0] || 'default') as LayoutStyle;
        const config: BlockConfig = { ...defaultConfig(), layoutStyle, title: def.label };
        const settings = buildSettingsFromConfig(def.type, config as any);

        createBlock.mutate(
            {
                type: def.type,
                title: config.title,
                layoutStyle,
                enabled: true,
                contentSource: { type: 'custom' },
                content: [],
                settings,
            },
            {
                onSuccess: (newBlock) => {
                    setShowBlockPicker(false);
                    if (newBlock) {
                        openEditor(newBlock)
                    }
                    showToast('Block added — edit it on the right');
                },
            }
        );
    };

    if (isLoading) return <AdminSkeleton />;
    if (error)
        return (
            <div className='p-8 text-red-600 text-sm'>
                Failed to load blocks: {error.message}
            </div>
        );

    return (
        <div className='min-h-screen bg-gray-50'>
            <ToastMessage />

            {/* Top Bar */}
            <div className='bg-white border-b border-gray-100 sticky top-0 z-40'>
                <div className='px-6 h-14 flex items-center justify-between' style={{ paddingRight: editingBlock ? PANEL_WIDTH + 24 : 24, transition: 'padding-right .2s' }}>
                    <div>
                        <h1 className='text-base font-semibold text-gray-900 tracking-tight'>Page builder</h1>
                        <p className='text-[11px] text-gray-400 mt-0.5'>Hover a block to reorder, edit, hide, or delete it</p>
                    </div>
                    <div className='flex items-center gap-3'>
                        {reorderBlocks.isPending && (
                            <span className='text-xs text-blue-600 animate-pulse'>Saving order…</span>
                        )}
                        <button
                            onClick={() => setShowBlockPicker(true)}
                            className='flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors'
                        >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                            </svg>
                            Add block
                        </button>
                    </div>
                </div>
            </div>

            {/* Canvas */}
            <div style={{ paddingRight: editingBlock ? PANEL_WIDTH : 0, transition: 'padding-right .2s' }}>
                {displayBlocks.length === 0 ? (
                    <EmptyState onAdd={() => setShowBlockPicker(true)} />
                ) : (
                    <div>
                        {displayBlocks.map((block) => (
                            <CanvasBlock
                                key={block.id}
                                block={block}
                                liveConfig={block.id === editingBlockId ? editConfig : null}
                                isEditing={block.id === editingBlockId}
                                isDragging={drag.draggedId === block.id}
                                isOver={drag.overId === block.id && drag.draggedId !== block.id}
                                onDragStart={(e) => handleDragStart(e, block.id)}
                                onDragOver={(e) => handleDragOver(e, block.id)}
                                onDrop={(e) => handleDrop(e, block.id)}
                                onDragEnd={handleDragEnd}
                                onEdit={() => openEditor(block)}
                                onToast={showToast}
                            />
                        ))}
                    </div>
                )}
                {displayBlocks.length > 0 && (
                    <div className='max-w-4xl mx-auto px-6 py-6'>
                        <button
                            onClick={() => setShowBlockPicker(true)}
                            className='w-full py-3.5 border border-dashed border-gray-300 rounded-xl text-gray-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all text-sm font-medium bg-white'
                        >
                            + Add another block
                        </button>
                    </div>
                )}
            </div>

            {/* Block Picker Modal */}
            {showBlockPicker && (
                <BlockPickerModal
                    onSelect={handleAddBlock}
                    onClose={() => setShowBlockPicker(false)}
                />
            )}

            {editingBlock && editConfig && editingDef && (
                <div
                    style={{
                        position: 'fixed', top: 0, right: 0, bottom: 0, width: PANEL_WIDTH, zIndex: 1000,
                        background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '0 0 30px rgba(0,0,0,0.08)',
                        display: 'flex', flexDirection: 'column',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <span style={{ fontSize: 20, flexShrink: 0 }}>{editingDef.icon}</span>
                            <div style={{ minWidth: 0 }}>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: 13.5, color: '#111827' }}>{editingDef.label}</p>
                                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>Editing live on the page</p>
                            </div>
                        </div>
                        <button onClick={closeEditor} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer', borderRadius: 8, color: '#9ca3af', flexShrink: 0 }}>
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <SharedConfigPanel
                            def={editingDef}
                            config={editConfig}
                            onChange={(p) => setEditConfig((c) => (c ? { ...c, ...p } : c))}
                            onSubmit={handleSaveEdit}
                            submitLabel="Save changes"
                            isSubmitting={updateBlock.isPending}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

interface CanvasBlockProps {
    block: AdminBlockConfig;
    liveConfig: BlockConfig | null;
    isEditing: boolean;
    isDragging: boolean;
    isOver: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: () => void;
    onEdit: () => void;
    onToast: (msg: string) => void;
}

function CanvasBlock({
    block, liveConfig, isEditing, isDragging, isOver,
    onDragStart, onDragOver, onDrop, onDragEnd, onEdit, onToast,
}: CanvasBlockProps) {
    const toggleBlock = useToggleBlock();
    const deleteBlock = useDeleteBlock();
    const [hovered, setHovered] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const Renderer = getBlockRenderer(block.type);
    const row = liveConfig ? configToRow(block.type, liveConfig, block.id) : block;

    const isContained = row.settings?.fullWidth === false;

    const showToolbar = hovered || isEditing || confirmDelete;


    return (
        <div
            onDragOver={onDragOver}
            onDrop={onDrop}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
            style={{
                position: 'relative',
                outline: isEditing ? '2px solid #3b82f6' : showToolbar ? '2px solid #bfdbfe' : '2px solid transparent',
                outlineOffset: -2,
                opacity: isDragging ? 0.4 : block.enabled ? 1 : 0.4,
                transition: 'outline-color .15s, opacity .15s',
                ...(isContained ? { padding: '20px 24px', background: '#f3f4f6' } : null),
            }}
        >
            {isOver && (
                <div style={{ position: 'absolute', top: -2, left: 0, right: 0, height: 3, background: '#3b82f6', zIndex: 25, borderRadius: 3 }} />
            )}

            {!block.enabled && (
                <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 25, background: '#111827', color: 'white', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', padding: '3px 10px', borderRadius: 100 }}>
                    HIDDEN
                </div>
            )}

            {showToolbar && (
                <div style={{
                    position: 'absolute', top: 10, right: 10, zIndex: 25,
                    display: 'flex', alignItems: 'center', gap: 2,
                    background: 'white', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.18)', padding: 3,
                }}>
                    <span
                        draggable
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        title="Drag to reorder"
                        style={{
                            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 5, cursor: 'grab', color: '#4b5563',
                        }}
                    >
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                            <path d='M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-6 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z' />
                        </svg>
                    </span>
                    <ToolbarBtn title="Edit block" onClick={onEdit}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </ToolbarBtn>
                    <ToolbarBtn
                        title={block.enabled ? 'Hide block' : 'Show block'}
                        onClick={() => toggleBlock.mutate({ id: block.id, enabled: !block.enabled }, { onSuccess: () => onToast(block.enabled ? 'Block hidden' : 'Block shown') })}
                    >
                        {block.enabled ? (
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        ) : (
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.774 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                            </svg>
                        )}
                    </ToolbarBtn>
                    {confirmDelete ? (
                        <>
                            <button
                                onClick={() => deleteBlock.mutate(block.id, { onSuccess: () => onToast('Block removed') })}
                                style={{ fontSize: 11, fontWeight: 700, color: 'white', background: '#dc2626', border: 'none', borderRadius: 5, padding: '5px 8px', cursor: 'pointer' }}
                            >
                                Confirm
                            </button>
                            <ToolbarBtn title="Cancel" onClick={() => setConfirmDelete(false)}>
                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </ToolbarBtn>
                        </>
                    ) : (
                        <ToolbarBtn title="Delete block" danger onClick={() => setConfirmDelete(true)}>
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </ToolbarBtn>
                    )}
                </div>
            )}

            {Renderer ? (
                isContained ? (
                    <div style={{ maxWidth: CONTAINED_MAX_WIDTH, margin: '0 auto', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.1)' }}>
                        <Renderer row={row} />
                    </div>
                ) : (
                    <Renderer row={row} />
                )
            ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {`No renderer registered for block type "${block.type}" yet.`}
                </div>
            )}
        </div>
    );
}

function ToolbarBtn({ children, title, onClick, onMouseDown, cursor, danger }: {
    children: React.ReactNode;
    title: string;
    onClick?: () => void;
    onMouseDown?: () => void;
    cursor?: string;
    danger?: boolean;
}) {
    return (
        <button
            title={title}
            onClick={onClick}
            style={{
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', borderRadius: 5, cursor: cursor || (onClick ? 'pointer' : 'default'),
                color: danger ? '#dc2626' : '#4b5563',
            }}
            onMouseDown={(e) => { e.preventDefault(); onMouseDown?.(); }}
        >
            {children}
        </button>
    );
}

// HELPERS

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className='text-center py-24'>
            <div className='text-5xl mb-4'>📦</div>
            <h3 className='text-base font-semibold text-gray-700 mb-2'>No blocks yet</h3>
            <p className='text-gray-400 mb-6 text-sm'>Add your first content block to start building your page.</p>
            <button onClick={onAdd} className='px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors'>
                + Add first block
            </button>
        </div>
    );
}

function AdminSkeleton() {
    return (
        <div className='max-w-4xl mx-auto px-6 py-6 flex flex-col gap-2'>
            {[...Array(6)].map((_, i) => (
                <div key={i} className='h-[120px] bg-gray-100 rounded-xl animate-pulse' style={{ animationDelay: `${i * 60}ms` }} />
            ))}
        </div>
    );
}